import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, Chapter } from "./shared";

const CALLOUTS = [
  {
    id: "ask-callout-1",
    eyebrow: "READS THE DAY FIRST",
    body: "It opens every chat with the day's read, before you type a word.",
    side: "left",
    pos: "-left-[234px] top-[9%]",
    float: 5.2,
  },
  {
    id: "ask-callout-2",
    eyebrow: "KNOWS WHO YOU MEAN",
    body: "Tag the person. It reads your rhythm against theirs.",
    side: "right",
    pos: "-right-[196px] 2xl:-right-[234px] top-[40%]",
    float: 6.2,
  },
  {
    id: "ask-callout-3",
    eyebrow: "GIVES YOU THE HOUR",
    body: "The window to send it, and the tone to use when you do.",
    side: "left",
    pos: "-left-[234px] bottom-[15%]",
    float: 5.6,
  },
];

const FloatingCallout = ({ c, delay }) => (
  <Reveal delay={delay} className={`absolute z-10 hidden xl:block ${c.pos}`}>
    <motion.div
      animate={{ y: [0, -8, 0] }}
      transition={{ duration: c.float, repeat: Infinity, ease: "easeInOut" }}
      className={`flex items-center ${c.side === "right" ? "flex-row-reverse" : ""}`}
      data-testid={c.id}
    >
      <div className="w-52 border border-[#F2F0EB]/15 bg-[#141518]/85 backdrop-blur-md px-4 py-3.5">
        <p className="font-mono-brand text-[9px] tracking-[0.28em] text-[#FF4D0A] mb-1.5">{c.eyebrow}</p>
        <p className="text-xs text-[#C9CCD1] leading-relaxed">{c.body}</p>
      </div>
      <span className="h-px w-8 bg-[#FF4D0A]/60" />
      <span className="h-1.5 w-1.5 bg-[#FF4D0A]" />
    </motion.div>
  </Reveal>
);

export const Ask = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const phoneY = useTransform(scrollYProgress, [0, 1], ["5%", "-5%"]);
  const glowScale = useTransform(scrollYProgress, [0, 0.5, 1], [0.8, 1.05, 0.9]);

  return (
    <section
      ref={ref}
      className="relative py-20 sm:py-28 lg:py-36 bg-[#0B0B0C] border-y border-[#F2F0EB]/5 overflow-hidden"
      data-testid="ask-section"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse 50% 55% at 66% 50%, rgba(255,77,10,0.09), transparent 70%)" }}
      />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <Chapter num="06" label="Ask the source" />
        </Reveal>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-10 items-center">
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="h-heavy text-[32px] sm:text-5xl lg:text-6xl leading-[0.95]" data-testid="ask-headline">
                A chat that
                <br />
                <span className="text-[#6E7178]">knows your rhythm.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-8 text-[#9DA1A8] leading-relaxed max-w-md" data-testid="ask-body">
                The job, the person, the timing. Answers grounded in your real
                energy today, from a voice that tells you straight. No candles.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-8 font-mono-brand text-[10px] tracking-[0.3em] text-[#6E7178]" data-testid="ask-mono-line">
                YOUR RHYTHM · THEIR RHYTHM · TODAY
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <div className="relative w-full">
              <motion.div
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[420px] sm:w-[520px] aspect-square pointer-events-none"
                style={{ scale: glowScale, background: "radial-gradient(circle, rgba(255,77,10,0.16), transparent 62%)" }}
              />
              <motion.div style={{ y: phoneY }} className="relative mx-auto w-[264px] sm:w-[300px] lg:w-[330px]" data-testid="ask-phone">
                <div className="rounded-[2.4rem] border border-[#2A2C30] bg-black p-[7px] shadow-[0_40px_90px_-30px_rgba(0,0,0,0.9)]">
                  <img
                    src="/brand/ask-screen.webp"
                    alt="The SOURCECODED Ask screen. The day reads 80, peak. Asked how to approach a Monday pitch and how Susan is today, it answers with the hour to send and the tone to use."
                    className="w-full rounded-[1.9rem]"
                    loading="lazy"
                    data-testid="ask-phone-image"
                  />
                </div>
                {CALLOUTS.map((c, i) => (
                  <FloatingCallout key={c.id} c={c} delay={0.15 + i * 0.15} />
                ))}
              </motion.div>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3 xl:hidden" data-testid="ask-callouts-mobile">
              {CALLOUTS.map((c) => (
                <div key={c.id} className="border border-[#F2F0EB]/[0.12] bg-[#141518] px-4 py-3.5">
                  <p className="font-mono-brand text-[9px] tracking-[0.28em] text-[#FF4D0A] mb-1.5">{c.eyebrow}</p>
                  <p className="text-xs text-[#9DA1A8] leading-relaxed">{c.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
